import { toString } from "lodash";
import Board from "@bonliva/react-kanban";
import { makeStyles } from "@material-ui/core";
import { Color } from "@bonliva-crm/ui";
import { BoardDescription, ColumnDescription, OnMoveCardParams } from "./kanban-types";

type Props<T extends { _id: string }> = {
  state: BoardDescription<T>;
  renderCard: (card: any, dragging: boolean) => React.ReactNode;
  onMoveCard?: (params: OnMoveCardParams) => Promise<void>;
  renderColumnHeader?: (column: ColumnDescription<T>) => React.ReactNode;
  disableCardDrag?: boolean;
};

export function KanbanBoard<T extends { _id: string }>(props: Props<T>) {
  useStyles({});

  const handleMoveCard = async (
    cardId: string,
    sourceColumnId: string,
    sourcePosition: number,
    destColumnId: string,
    destPosition: number
  ) => {
    const sourceCol = props.state.columns.find((c) => c.id === sourceColumnId);
    const destCol = props.state.columns.find((c) => c.id === destColumnId);
    const card = sourceCol.cards.find((c) => c.id === cardId);
    const issue = card.issue;
    const beforeCard = destCol.cards[destPosition];

    await props.onMoveCard({
      cardId,
      issueId: issue?._id,
      sourceColumnId,
      sourcePosition,
      sourceColumnName: sourceCol.title,
      destColumnId,
      destPosition,
      destColumnName: destCol.title,
      destBeforeCardId: beforeCard?.id ? toString(beforeCard.id) : null
    });
  };

  if (!props.state) {
    return null;
  }

  return (
    <Board
      style={{ height: "100%" }}
      allowAddColumn={false}
      allowRemoveColumn={false}
      allowRenameColumn={false}
      disableColumnDrag
      disableCardDrag={props.disableCardDrag ?? false}
      onCardDragEnd={(card, source, destination) => {
        handleMoveCard(
          card.id,
          source.fromColumnId,
          source.fromPosition,
          destination.toColumnId,
          destination.toPosition
        );
      }}
      renderCard={(card, _, dragging) => {
        return props.renderCard(card, dragging);
      }}
      renderColumnHeader={props.renderColumnHeader}
      getColumnClassName={(params) => {
        if (params.snapshot.isDraggingOver) {
          return "react-kanban-column-dragging-over";
        }

        return "";
      }}
    >
      {props.state}
    </Board>
  );
}

const useStyles = makeStyles({
  "@global": {
    ".react-kanban-board": {
      padding: 0,
      height: "100%",
      overflowY: "auto !important"
    },

    ".react-kanban-board > div:first-child": {
      display: "flex !important",
      alignItems: "stretch"
    },

    ".react-kanban-column": {
      padding: 0,
      backgroundColor: "#f8f8f8",
      margin: 0,
      minWidth: "15rem",
      minHeight: "600px !important"
    },

    ".react-kanban-column-dragging-over": {
      backgroundColor: Color.LightYellow,
      border: `1px solid ${Color.Yellow}`,
      borderRadius: "4px",
      boxSizing: "content-box",
      boxShadow: "inset 0 0 0 2px #f0f0f0"
    },

    ".react-kanban-column > div:first-child": {
      position: "sticky",
      top: 0,
      left: 0,
      zIndex: 2
    },

    ".react-kanban-column:not(:last-child)": {
      marginRight: "8px"
    },

    ".react-kanban-column-header": {
      borderRadius: "8px 8px 0 0",
      padding: "8px 16px",
      backgroundColor: "#8c9ab2",
      color: "white",
      display: "flex",
      alignItems: "center",
      marginBottom: "8px"
    },

    ".react-kanban-card ": {
      margin: "4px"
    }
  }
});
